import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SparkleEffect } from '../components/SparkleEffect';
import { GlitterHeart } from '../components/GlitterHeart';
import { BookOpenIcon, PlaneIcon, ArrowRightIcon, FlameIcon } from 'lucide-react';
export function HomePage() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const marqueePhrases = ['No toxic positivity', 'Real talk only', 'From broken to badass', 'Healing is messy', 'Take up space, babe'];
  return <div className="min-h-screen overflow-hidden" data-id="element-290">
      <style data-id="element-291">{`
        @keyframes marquee {
          0% { transform: translateX(0%); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 25s linear infinite;
        }
      `}</style>

      {/* HERO SECTION - Split Layout */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-id="element-292">
        {/* Dramatic Background Blobs */}
        <div className="absolute inset-0 bg-gradient-to-br from-blush-50 via-white to-lavender-50 -z-20" data-id="element-293" />
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-blush-300/30 rounded-full blur-[120px] -z-10 -translate-x-1/2 -translate-y-1/4" data-id="element-294" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-lavender-300/30 rounded-full blur-[100px] -z-10 translate-x-1/3 translate-y-1/3" data-id="element-295" />
        <div className="absolute top-1/2 left-1/2 w-[800px] h-[400px] bg-cta-300/10 rounded-full blur-[100px] -z-10 -translate-x-1/2 -translate-y-1/2 rotate-45" data-id="element-296" />

        <SparkleEffect count={150} data-id="element-297" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-298">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-299">
            {/* Left Content */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="text-center lg:text-left pt-10" data-id="element-300">
              <motion.div variants={itemVariants} className="mb-8 inline-block" data-id="element-301">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/80 backdrop-blur-md border border-blush-300 text-sm font-bold text-plum-700 shadow-lg shadow-blush-200/50 uppercase tracking-wider transform -rotate-2" data-id="element-302">
                  <GlitterHeart size={18} data-id="element-303" />
                  Real talk. Real healing. Real results.
                </span>
              </motion.div>

              <motion.h1 variants={itemVariants} className="font-heading text-6xl md:text-8xl font-bold mb-6 tracking-tight leading-[1.1]" data-id="element-304">
                <span className="glitter-text block pb-2 bg-[#4A2D52] text-[#2D1533]" data-id="element-305">
                  Trippin'
                </span>
                <span className="block text-[#D63385] text-[56px]" data-id="element-306">
                  Through Life
                </span>
                <span className="md:text-6xl block mt-2 text-[#2D1533] text-[80px]" data-id="element-307">
                  with Mandy
                </span>
              </motion.h1>

              <motion.p variants={itemVariants} className="text-xl md:text-2xl text-plum-500 max-w-xl mx-auto lg:mx-0 mb-10 font-medium leading-relaxed" data-id="element-308">
                Life knocked me down, but I got back up covered in glitter. Now
                I'm here to help you do the same.
              </motion.p>

              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-5" data-id="element-309">
                <Link to="/coaching" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/40 hover:-translate-y-1 hover:scale-105" data-id="element-310">
                  <GlitterHeart size={22} variant="light" data-id="element-311" />
                  Work With Me
                </Link>
                <Link to="/courses" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-white text-plum-700 font-bold text-lg border-2 border-blush-300 hover:bg-blush-50 hover:border-cta-400 transition-all shadow-lg hover:-translate-y-1" data-id="element-312">
                  <GlitterHeart size={22} data-id="element-313" />
                  Explore Courses
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Decorative Composition */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8,
            rotate: -10
          }} animate={{
            opacity: 1,
            scale: 1,
            rotate: 0
          }} transition={{
            duration: 1,
            ease: 'easeOut',
            delay: 0.2
          }} className="relative hidden lg:flex justify-center items-center h-[600px]" data-id="element-314">
              {/* Main massive heart */}
              <motion.div animate={{
              y: [-10, 10, -10]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative z-20" data-id="element-315">
                <GlitterHeart size={320} className="drop-shadow-2xl filter" data-id="element-316" />
              </motion.div>

              {/* Orbiting smaller hearts */}
              <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 20,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-0 z-10" data-id="element-317">
                <div className="absolute top-[10%] left-[20%] -rotate-12" data-id="element-318">
                  <GlitterHeart size={60} data-id="element-319" />
                </div>
                <div className="absolute bottom-[15%] right-[15%] rotate-45" data-id="element-320">
                  <GlitterHeart size={80} data-id="element-321" />
                </div>
                <div className="absolute top-[40%] right-[5%] rotate-12" data-id="element-322">
                  <GlitterHeart size={40} data-id="element-323" />
                </div>
              </motion.div>

              {/* Glassmorphism floating stat card */}
              <motion.div animate={{
              y: [10, -10, 10]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} className="absolute bottom-20 left-0 z-30 bg-white/70 backdrop-blur-xl border border-white p-6 rounded-[2rem] shadow-2xl transform -rotate-6" data-id="element-324">
                <div className="flex items-center gap-4" data-id="element-325">
                  <div className="w-12 h-12 rounded-full bg-cta-100 flex items-center justify-center" data-id="element-326">
                    <FlameIcon className="w-6 h-6 text-[#2D1533]" data-id="element-327" />
                  </div>
                  <div data-id="element-328">
                    <p className="font-heading text-2xl font-bold text-plum-700" data-id="element-329">
                      100% Real
                    </p>
                    <p className="text-sm font-bold text-plum-500 uppercase tracking-wider" data-id="element-330">
                      No BS Allowed
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* SASSY MARQUEE */}
      <div className="relative py-6 bg-plum-900 overflow-hidden transform -rotate-1 scale-105 shadow-2xl z-20" data-id="element-331">
        <div className="absolute inset-0 bg-gradient-to-r from-cta-500/20 to-plum-500/20 mix-blend-overlay" data-id="element-332" />
        <div className="flex whitespace-nowrap animate-marquee" data-id="element-333">
          {/* Double the array for seamless looping */}
          {[...marqueePhrases, ...marqueePhrases, ...marqueePhrases, ...marqueePhrases].map((phrase, i) => <div key={i} className="flex items-center gap-8 mx-8" data-id="element-334">
              <span className="font-heading text-2xl md:text-3xl font-bold text-white tracking-wide" data-id="element-335">
                {phrase}
              </span>
              <GlitterHeart size={24} variant="light" data-id="element-336" />
            </div>)}
        </div>
      </div>

      {/* WHAT I OFFER - Bento Grid */}
      <section className="py-32 bg-blush-50 relative" data-id="element-337">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white rounded-full blur-[100px] -z-10 translate-x-1/2 -translate-y-1/2" data-id="element-338" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-339">
          <div className="text-center mb-20" data-id="element-340">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="inline-flex items-center justify-center gap-3 mb-6" data-id="element-341">
              <GlitterHeart size={24} data-id="element-342" />
              <h2 className="font-heading text-5xl md:text-6xl font-bold text-plum-700" data-id="element-343">
                How We're Doing This
              </h2>
              <GlitterHeart size={24} data-id="element-344" />
            </motion.div>
            <p className="text-plum-500 max-w-2xl mx-auto text-xl font-medium" data-id="element-345">
              No fluff. No toxic positivity. Just real tools to help you stop
              surviving and start thriving.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 auto-rows-[minmax(250px,auto)]" data-id="element-346">
            {/* Coaching Card - FEATURED (Spans 2 cols on desktop) */}
            <motion.div variants={itemVariants} className="lg:col-span-2 lg:row-span-2 group relative bg-gradient-to-br from-plum-900 via-plum-800 to-plum-900 p-10 md:p-14 rounded-[3rem] shadow-2xl overflow-hidden flex flex-col justify-between" data-id="element-347">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-cta-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-348" />
              <div className="absolute bottom-0 left-0 w-80 h-80 bg-lavender-500/20 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3" data-id="element-349" />
              <SparkleEffect count={40} data-id="element-350" />

              <div className="absolute top-8 right-8 opacity-20 group-hover:opacity-40 transition-opacity duration-500 group-hover:scale-110 transform" data-id="element-351">
                <GlitterHeart size={120} variant="light" data-id="element-352" />
              </div>

              <div className="relative z-10 mb-12" data-id="element-353">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-bold mb-8 uppercase tracking-wider" data-id="element-354">
                  <FlameIcon className="w-4 h-4 text-cta-400" data-id="element-355" />
                  Signature Experience
                </div>
                <h3 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-356">
                  1:1 Coaching
                </h3>
                <p className="text-plum-100 text-xl max-w-md leading-relaxed font-medium" data-id="element-357">
                  Personalized, no-BS coaching to help you stop surviving and
                  start thriving. We dig deep and do the real work.
                </p>
              </div>

              <div className="relative z-10 mt-auto" data-id="element-358">
                <Link to="/coaching" className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-cta-500 text-white font-bold text-lg hover:bg-cta-400 transition-all shadow-lg hover:-translate-y-1 w-full sm:w-auto" data-id="element-359">
                  <GlitterHeart size={20} variant="light" data-id="element-360" />
                  Work With Me
                  <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-361" />
                </Link>
              </div>
            </motion.div>

            {/* Courses Card */}
            <motion.div variants={itemVariants} className="group relative bg-gradient-to-br from-white to-blush-50 p-8 md:p-10 rounded-[3rem] shadow-xl border border-blush-200 overflow-hidden flex flex-col justify-between hover:-translate-y-2 transition-transform duration-500" data-id="element-362">
              <div className="absolute -right-6 -top-6 opacity-10 group-hover:opacity-20 transition-opacity duration-500" data-id="element-363">
                <BookOpenIcon className="w-40 h-40 text-cta-500" data-id="element-364" />
              </div>

              <div className="relative z-10 mb-8" data-id="element-365">
                <div className="w-16 h-16 rounded-2xl bg-blush-200 flex items-center justify-center mb-6 shadow-inner" data-id="element-366">
                  <BookOpenIcon className="w-8 h-8 text-plum-700" data-id="element-367" />
                </div>
                <h3 className="font-heading text-3xl font-bold text-plum-700 mb-4" data-id="element-368">
                  Courses
                </h3>
                <p className="text-plum-600 font-medium leading-relaxed" data-id="element-369">
                  From Broken to Badass and beyond. Self-paced courses that
                  don't sugarcoat the journey.
                </p>
              </div>

              <Link to="/courses" className="relative z-10 inline-flex items-center font-bold text-cta-500 hover:text-cta-600 text-lg mt-auto" data-id="element-370">
                <GlitterHeart size={18} className="mr-2" data-id="element-371" />
                Get Started
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-372" />
              </Link>
            </motion.div>

            {/* Travel Card */}
            <motion.div variants={itemVariants} className="group relative bg-gradient-to-br from-white to-sky-50 p-8 md:p-10 rounded-[3rem] shadow-xl border border-sky-200 overflow-hidden flex flex-col justify-between hover:-translate-y-2 transition-transform duration-500" data-id="element-373">
              <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:opacity-20 transition-opacity duration-500" data-id="element-374">
                <PlaneIcon className="w-40 h-40 text-sky-500" data-id="element-375" />
              </div>

              <div className="relative z-10 mb-8" data-id="element-376">
                <div className="w-16 h-16 rounded-2xl bg-sky-200 flex items-center justify-center mb-6 shadow-inner" data-id="element-377">
                  <PlaneIcon className="w-8 h-8 text-plum-700" data-id="element-378" />
                </div>
                <h3 className="font-heading text-3xl font-bold text-plum-700 mb-4" data-id="element-379">
                  Travel
                </h3>
                <p className="text-plum-600 font-medium leading-relaxed" data-id="element-380">
                  Budget-friendly getaways because everyone deserves to escape,
                  reset, and explore.
                </p>
              </div>

              <Link to="/travel" className="relative z-10 inline-flex items-center font-bold text-cta-500 hover:text-cta-600 text-lg mt-auto" data-id="element-381">
                <GlitterHeart size={18} className="mr-2" data-id="element-382" />
                Plan a Trip
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-2 transition-transform" data-id="element-383" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* DRAMATIC QUOTE SECTION */}
      <section className="py-32 bg-gradient-to-br from-plum-900 via-plum-800 to-plum-900 relative overflow-hidden" data-id="element-384">
        <SparkleEffect count={120} data-id="element-385" />

        {/* Decorative giant quotes */}
        <div className="absolute top-10 left-10 text-[20rem] font-heading text-white/5 leading-none select-none pointer-events-none" data-id="element-386">
          "
        </div>
        <div className="absolute bottom-0 right-10 text-[20rem] font-heading text-white/5 leading-none select-none pointer-events-none rotate-180" data-id="element-387">
          "
        </div>

        <div className="max-w-5xl mx-auto px-4 text-center relative z-10" data-id="element-388">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="flex flex-col items-center" data-id="element-389">
            <div className="flex items-center justify-center gap-6 mb-12" data-id="element-390">
              <GlitterHeart size={48} variant="light" className="animate-pulse" data-id="element-391" />
              <GlitterHeart size={64} variant="light" data-id="element-392" />
              <GlitterHeart size={48} variant="light" className="animate-pulse" data-id="element-393" />
            </div>

            <blockquote className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-tight mb-12 drop-shadow-xl" data-id="element-394">
              "You didn't survive all that shit just to live small. It's time to
              <span className="text-cta-400 block mt-4" data-id="element-395">
                take up space, babe.
              </span>
              "
            </blockquote>

            <div className="w-24 h-1.5 bg-gradient-to-r from-transparent via-cta-400 to-transparent rounded-full" data-id="element-396" />
          </motion.div>
        </div>
      </section>

      {/* NEWSLETTER - Glassmorphism */}
      <section className="py-32 bg-gradient-to-b from-blush-50 to-white relative overflow-hidden" data-id="element-397">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-[400px] bg-cta-300/20 rounded-full blur-[120px] -z-10" data-id="element-398" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-399">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="bg-white/40 backdrop-blur-2xl border-2 border-white/60 rounded-[3rem] p-10 md:p-20 text-center relative shadow-2xl" data-id="element-400">
            {/* Floating decorative hearts */}
            <div className="absolute -top-8 -left-8 rotate-12" data-id="element-401">
              <GlitterHeart size={80} data-id="element-402" />
            </div>
            <div className="absolute -bottom-10 -right-10 -rotate-12" data-id="element-403">
              <GlitterHeart size={100} data-id="element-404" />
            </div>
            <div className="absolute top-10 right-10 opacity-50" data-id="element-405">
              <GlitterHeart size={40} data-id="element-406" />
            </div>

            <div className="relative z-10 max-w-2xl mx-auto" data-id="element-407">
              <h2 className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-408">
                Stay Connected
              </h2>
              <p className="text-plum-600 text-xl mb-10 font-medium leading-relaxed" data-id="element-409">
                Get real talk, course drops, and travel deals straight to your
                inbox.{' '}
                <strong className="text-cta-500" data-id="element-410">
                  No fluff, just the good stuff.
                </strong>
              </p>

              <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" onSubmit={e => e.preventDefault()} data-id="element-411">
                <input type="email" placeholder="Enter your email, gorgeous..." className="flex-1 px-8 py-5 rounded-full border-2 border-white/80 bg-white/50 backdrop-blur-sm focus:bg-white focus:ring-4 focus:ring-cta-500/20 focus:border-cta-400 shadow-inner outline-none text-plum-700 text-lg placeholder:text-plum-400 transition-all font-medium" required data-id="element-412" />
                <button type="submit" className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full bg-plum-700 text-white font-bold text-lg hover:bg-plum-900 transition-all shadow-xl hover:-translate-y-1 hover:shadow-plum-700/30" data-id="element-413">
                  <GlitterHeart size={20} variant="light" data-id="element-414" />
                  I'm In
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}